// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getWishListLinesDataView } from "@insite/client-framework/Store/Data/WishListLines/WishListLinesSelectors";
import addToWishList, {
    AddToWishListParameter,
} from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import { getWishListState } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import loadWishListIfNeeded from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/LoadWishListIfNeeded";
import loadWishListLines from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/LoadWishListLines";
import setAllWishListLinesIsSelected from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/SetAllWishListLinesIsSelected";
import setEditingSortOrder from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/SetEditingSortOrder";
import updateLoadWishListLinesParameter from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateLoadWishListLinesParameter";
import translate from "@insite/client-framework/Translate";
import { WishListModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { OrderUploadStyles } from "@insite/content-library/Components/OrderUpload";
import { OrderUploadErrorsModalStyles } from "@insite/content-library/Components/OrderUploadErrorsModal";
import { CheckboxProps } from "@insite/mobius/Checkbox";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconProps } from "@insite/mobius/Icon";
import Search from "@insite/mobius/Icons/Search";
import { OverflowMenuProps } from "@insite/mobius/OverflowMenu";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import debounce from "lodash/debounce";
import React, { ChangeEvent, useCallback, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

import { AccordionPresentationProps } from "@insite/mobius/Accordion";
import SelectInline, { SelectInlineStyles } from "@store/Library/Components/Toolkit/SelectInline";
import CardListHeading from "@store/Overrides/Components/CardListHeading";
import { ProductSelectorStyles } from "@store/Overrides/Components/ProductSelector";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { FormFieldIcon } from "@store/MobiusOverrides/FormField/FormField";

const mapStateToProps = (state: ApplicationState) => ({
    wishListSettings: getSettingsCollection(state).wishListSettings,
    wishListDataView: getWishListState(state, state.pages.myListDetails.wishListId),
    wishListLinesDataView: getWishListLinesDataView(state, state.pages.myListDetails.loadWishListLinesParameter),
    loadWishListLinesParameter: state.pages.myListDetails.loadWishListLinesParameter,
    selectedWishListLineIds: state.pages.myListDetails.selectedWishListLineIds,
    editingSortOrder: state.pages.myListDetails.editingSortOrder,
});

const mapDispatchToProps = {
    updateLoadWishListLinesParameter,
    loadWishListIfNeeded,
    loadWishListLines,
    setAllWishListLinesIsSelected,
    addToWishList: makeHandlerChainAwaitable<AddToWishListParameter, WishListModel>(addToWishList),
    setEditingSortOrder,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface MyListDetailsSearchStyles {
    wrapper?: InjectableCss;
    leftColumnWrapper?: InjectableCss;
    rightColumnWrapper?: InjectableCss;
    container?: GridContainerProps;
    selectAllGridItem?: GridItemProps;
    selectAll?: CheckboxProps;
    editSortOrder?: CheckboxProps;
    searchGridItem?: GridItemProps;
    productSelectorGridItem?: GridItemProps;
    productSelectorStyles?: ProductSelectorStyles;
    orderUploadStyles?: OrderUploadStyles;
    orderUploadErrorsModalStyles?: OrderUploadErrorsModalStyles;
    search?: TextFieldProps;
    sortByGridItem?: GridItemProps;
    sortByOverflowMenu?: OverflowMenuProps;
    sortByClickable?: ClickablePresentationProps;
    selectedSortByClickable?: ClickablePresentationProps;
    closeAddItemsToList?: IconProps;
    // Valtech Custom Code
    addItemsAccordion?: AccordionPresentationProps;
    sortByWrapper?: InjectableCss;
    sortBySelectInline?: SelectInlineStyles;
}

export const myListsDetailsSearchStyles: MyListDetailsSearchStyles = {
    wrapper: {
        css: css`
            ${x => spacingStyles(null, x.theme.design.typography.spacing.large)};
        `,
    },
    addItemsAccordion: {
        css: css`
            ${x => spacingStyles(null, x.theme.design.typography.spacing.larger)};
        `,
    },
    searchGridItem: {
        width: [12, 12, 7, 7, 7],
        // Bunzl custom code, fix search overlap
        css: css`
            input:not(:placeholder-shown)::-webkit-search-cancel-button {
                margin-bottom: 3px;
                padding: 2px;
                margin-right: 1.75rem;
            }
        `,
    },
    search: {
        iconProps: { src: Search },
    },

    sortByWrapper: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.smaller};
        `,
    },
    sortBySelectInline: {
        select: {
            cssOverrides: {
                formInputWrapper: css`
                    padding-bottom: ${pxToRem(5)};
                    ${FormFieldIcon} {
                        padding-bottom: ${pxToRem(13)};
                    }
                `,
            },
        },
    },
};

const styles = myListsDetailsSearchStyles;

const MyListDetailsSearch = ({
    wishListSettings,
    wishListDataView,
    wishListLinesDataView,
    loadWishListLinesParameter,
    updateLoadWishListLinesParameter,
    loadWishListLines,
    setEditingSortOrder,
}: Props) => {
    const [query, setQuery] = useState(loadWishListLinesParameter.query || ""); // Valtech Custom Code

    useEffect(() => {
        setQuery("");
    }, []);

    // bunzl custom code
    const debouncedSearch = useCallback(
        (query: string) =>
            debounce(() => {
                if (!wishListDataView.value) {
                    return;
                }

                updateLoadWishListLinesParameter({ query });
                loadWishListLines();
            }, 200)(),
        [loadWishListLines, updateLoadWishListLinesParameter, wishListDataView.value],
    );

    if (!wishListDataView.value || !wishListSettings) {
        return null;
    }

    const wishList = wishListDataView.value;

    const searchChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const newQuery = event.target.value || "";
        setQuery(newQuery);
        debouncedSearch(newQuery);
    };

    const sortByChangeHandler = (sortBy: string) => {
        localStorage.setItem("wishListDetailSortBy", sortBy);
        setEditingSortOrder({ editingSortOrder: false });
        updateLoadWishListLinesParameter({ sort: sortBy });
        loadWishListLines();
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <CardListHeading heading={translate("My List")} hasNoResults>
                {wishListLinesDataView.value &&
                wishListLinesDataView.pagination?.sortOptions &&
                !wishList.isAutogenerated ? (
                    <StyledWrapper {...styles.sortByWrapper} data-test-selector="myListDetailPage_ProductsSortBy">
                        <SelectInline
                            label={`${translate("Sort by")}${translate(":")}`}
                            labelPosition="left"
                            uid="myLists_sort"
                            value={loadWishListLinesParameter.sort}
                            extendedStyles={styles.sortBySelectInline}
                            onChange={event => sortByChangeHandler(event.currentTarget.value)}
                        >
                            {wishListLinesDataView.value &&
                                wishListLinesDataView.pagination?.sortOptions?.map(sortOption => (
                                    <option value={sortOption.sortType} key={sortOption.sortType}>
                                        {sortOption.displayName}
                                    </option>
                                ))}
                        </SelectInline>
                    </StyledWrapper>
                ) : null}
            </CardListHeading>
            <GridContainer {...styles.container}>
                <GridItem {...styles.searchGridItem}>
                    <TextField
                        {...styles.search}
                        placeholder={translate("Type to search")}
                        value={query}
                        onChange={searchChangeHandler}
                        data-test-selector="wishListSearch"
                        label={translate("Search within list")}
                        type="search"
                    />
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(MyListDetailsSearch),
    definition: {
        group: "My Lists Details",
        displayName: "Details Search",
        allowedContexts: ["MyListsDetailsPage"],
    },
};

export default widgetModule;
